/**
 * Renders multi-page PDFs on mobile using PDF.js.
 * Mobile browsers only show the first page of PDFs embedded in iframes.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import * as pdfjs from 'pdfjs-dist'
import { openPdfInNativeViewer } from '../utils/deviceUtils'

pdfjs.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`

const MobilePDFViewer = ({ blobUrl, fileName = 'proforma.pdf'  }) => {
  const containerRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pageCount, setPageCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    const container = containerRef.current

    const renderPdf = async () => {
      if (!blobUrl || !container) return

      setLoading(true)
      setError(null)
      container.innerHTML = ''

      try {
        const loadingTask = pdfjs.getDocument(blobUrl)
        const pdf = await loadingTask.promise

        if (cancelled) return

        setPageCount(pdf.numPages)

        const containerWidth = container.clientWidth || window.innerWidth
        const pixelRatio = window.devicePixelRatio || 1

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return

          const page = await pdf.getPage(pageNum)
          const unscaledViewport = page.getViewport({ scale: 1  })
          const scale = containerWidth / unscaledViewport.width
          const viewport = page.getViewport({ scale  })

          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          canvas.width = Math.floor(viewport.width * pixelRatio)
          canvas.height = Math.floor(viewport.height * pixelRatio)
          canvas.style.width = `${viewport.width}px`
          canvas.style.height = `${viewport.height}px`
          canvas.style.display = 'block'
          canvas.style.margin = '0 auto 8px'

          context.scale(pixelRatio, pixelRatio)

          const pageWrapper = document.createElement('div')
          pageWrapper.style.width = '100%'
          pageWrapper.appendChild(canvas)
          container.appendChild(pageWrapper)

          await page.render({
            canvasContext: context,
            canvas,
            viewport,
          }).promise
        }
      } catch (renderError) {
        console.error('Mobile PDF render failed:', renderError)
        if (!cancelled) {
          setError('Unable to display the full proforma in your browser.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void renderPdf()

    return () => {
      cancelled = true
      if (container) {
        container.innerHTML = ''
      }
    }
  }, [blobUrl])

  const handleOpenNative = () => {
    openPdfInNativeViewer(blobUrl, fileName)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {pageCount > 0
            ? `${pageCount} page${pageCount === 1 ? '' : 's'}`
            : 'Proforma'}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleOpenNative}
        >
          Open PDF
        </Button>
      </Box>

      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleOpenNative}>
            Open Full Proforma
          </Button>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          bgcolor: '#f5f5f5',
          pb: 2,
        }}
      />
    </Box>
  );
}

export default MobilePDFViewer
